import { COMPANION_RUNTIME_KINDS, type CompanionRuntimeKind } from './CompanionRuntime';
import { EXTERNAL_PARTICIPANT_TYPES } from './ExternalParticipants';
import {
  ACCESS_POLICIES,
  CONTRIBUTION_MODES,
  type AccessPolicy,
  ORCHESTRATION_MODES,
  type OrchestrationMode,
  type RoomContributionPolicy,
  type RoomContributionPolicyOverride,
  type RoomDefinition,
  ROOM_EXPERIENCES,
  type RoomExperience,
  type RoomFeatureFlags,
  RENDERER_KINDS,
  type RendererKind,
  TRANSCRIPT_OWNERSHIP_MODES,
} from './RoomDefinition';
import {
  PARTICIPATION_LAYER_KINDS,
  type ParticipationCapabilities,
  type ParticipationLayerDefinition,
  PRESENCE_MODES,
} from './ParticipationLayer';
import { ROOM_KINDS, type RoomKind } from './Room';
import { TOWNHALL_ROOM_DEFINITION } from './Townhall';

const DEFAULT_ROOM_KIND: RoomKind = 'standard';

const PRIMARY_STAGE_PARTICIPATION: readonly ParticipationLayerDefinition[] = [
  {
    kind: 'stage_primary',
    label: 'Primary Stage',
    presenceMode: 'webrtc_bidirectional',
    capabilities: {
      canSpeakLive: true,
      canDialInAudio: false,
      canVote: false,
      canRespondToPolls: false,
      canChat: true,
      canRaiseHand: false,
      canRequestPromotion: false,
      canModeratePromotions: false,
      canModerateContributions: false,
      canMuteParticipants: false,
      canCloseCallInWindow: false,
      canManagePolls: false,
      canBlockAudienceSessions: false,
      receivesTranscript: true,
      receivesRealtimeState: true,
    },
  },
];

const BOARD_ROOM_PARTICIPATION: readonly ParticipationLayerDefinition[] = [
  {
    kind: 'stage_primary',
    label: 'Board Floor',
    presenceMode: 'webrtc_bidirectional',
    capabilities: {
      canSpeakLive: true,
      canDialInAudio: false,
      canVote: true,
      canRespondToPolls: true,
      canChat: true,
      canRaiseHand: false,
      canRequestPromotion: false,
      canModeratePromotions: false,
      canModerateContributions: false,
      canMuteParticipants: false,
      canCloseCallInWindow: false,
      canManagePolls: false,
      canBlockAudienceSessions: false,
      receivesTranscript: true,
      receivesRealtimeState: true,
    },
  },
];

const CONTRIBUTION_PARTICIPANT_TYPES = [
  'human',
  'internal_companion',
  'external_participant',
  ...EXTERNAL_PARTICIPANT_TYPES,
] as const;

const FEATURE_FLAG_KEYS: Array<keyof RoomFeatureFlags> = [
  'requiresAuthGate',
  'supportsSpectators',
  'supportsCallInAudio',
  'supportsPolls',
  'supportsVoting',
  'supportsRealtimeTranscript',
  'supportsCompanionAgents',
  'supportsPresentationOverlay',
  'supportsPremiumAvatars',
  'supportsWebStage',
  'supportsProgramManifest',
  'supportsBroadcast',
  'supportsRecording',
  'supportsSharedStageMedia',
  'supportsStageFeedback',
  'supportsSkills',
];

export interface PersistedRoomDefinitionSource {
  id: string;
  roomType?: string | null;
  isPrivate?: boolean | null;
}

export interface RoomDefinitionExtension {
  experience?: RoomExperience;
  accessPolicy?: AccessPolicy;
  orchestrationMode?: OrchestrationMode;
  participation?: readonly ParticipationLayerDefinition[];
  companionRuntime?: CompanionRuntimeKind;
  contribution?: RoomContributionPolicy;
  renderer?: RendererKind;
  features?: Partial<RoomFeatureFlags>;
}

export interface BuildRoomDefinitionInput {
  room: PersistedRoomDefinitionSource;
  workflowConfiguration?: Record<string, unknown> | null;
  roomExtension?: RoomDefinitionExtension | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isOneOf = <T extends readonly string[]>(
  values: T,
  value: unknown,
): value is T[number] => (
  typeof value === 'string' && values.includes(value as T[number])
);

const normalizeRoomKind = (roomType?: string | null): RoomKind => {
  if (roomType && ROOM_KINDS.includes(roomType as RoomKind)) {
    return roomType as RoomKind;
  }

  return DEFAULT_ROOM_KIND;
};

const getDefaultAccessPolicy = (
  roomKind: RoomKind,
  isPrivate?: boolean | null,
): AccessPolicy => {
  if (roomKind === 'townhall') {
    return 'broadcast_audience';
  }

  return isPrivate === false ? 'public_join' : 'private_auth';
};

const createRoomContributionPolicy = (
  defaultMode: (typeof CONTRIBUTION_MODES)[number],
  allowedRuntimeKinds: readonly CompanionRuntimeKind[],
  options?: {
    allowedModes?: readonly (typeof CONTRIBUTION_MODES)[number][];
    transcriptOwnership?: RoomContributionPolicy['transcriptOwnership'];
    overrides?: readonly RoomContributionPolicyOverride[];
  },
): RoomContributionPolicy => ({
  defaultMode,
  allowedModes: options?.allowedModes ?? [defaultMode],
  allowedRuntimeKinds,
  transcriptOwnership: options?.transcriptOwnership ?? 'telegamez',
  overrides: options?.overrides,
});

const getBaseRoomDefinition = (
  roomKind: RoomKind,
  isPrivate?: boolean | null,
): RoomDefinition => {
  switch (roomKind) {
    case 'talk_show':
      return {
        kind: roomKind,
        experience: 'panel_show',
        accessPolicy: getDefaultAccessPolicy(roomKind, isPrivate),
        orchestrationMode: 'multi_agent_routed',
        participation: PRIMARY_STAGE_PARTICIPATION,
        companionRuntime: 'telegamez_multi_companion',
        contribution: createRoomContributionPolicy(
          'recorded_audio_submission',
          ['telegamez_multi_companion'],
          {
            overrides: [
              {
                layer: 'stage_primary',
                defaultMode: 'live_turn',
                allowedModes: ['live_turn'],
                allowedRuntimeKinds: ['telegamez_multi_companion'],
              },
              {
                participantType: 'internal_companion',
                defaultMode: 'live_turn',
                allowedModes: ['live_turn'],
                allowedRuntimeKinds: ['telegamez_multi_companion'],
              },
              {
                participantType: 'external_participant',
                defaultMode: 'text_prompt_submission',
                allowedModes: [
                  'text_prompt_submission',
                  'live_turn',
                  'speech_to_speech_session',
                ],
                allowedRuntimeKinds: [
                  'external_text_agent',
                  'third_party_speech_to_speech',
                ],
                transcriptOwnership: 'hybrid',
              },
            ],
          },
        ),
        renderer: 'three_avatar',
        features: {
          requiresAuthGate: isPrivate !== false,
          supportsSpectators: false,
          supportsCallInAudio: true,
          supportsPolls: false,
          supportsVoting: false,
          supportsRealtimeTranscript: true,
          supportsCompanionAgents: true,
          supportsPresentationOverlay: false,
          supportsPremiumAvatars: false,
          supportsWebStage: false,
          supportsProgramManifest: false,
          supportsBroadcast: false,
          supportsRecording: false,
          supportsSharedStageMedia: false,
          supportsStageFeedback: false,
          supportsSkills: false,
        },
      };
    case 'board_room':
      return {
        kind: roomKind,
        experience: 'board_meeting',
        accessPolicy: getDefaultAccessPolicy(roomKind, isPrivate),
        orchestrationMode: 'multi_agent_scheduled',
        participation: BOARD_ROOM_PARTICIPATION,
        companionRuntime: 'telegamez_multi_companion',
        contribution: createRoomContributionPolicy(
          'live_turn',
          ['telegamez_multi_companion'],
          {
            overrides: [
              {
                participantType: 'external_participant',
                defaultMode: 'text_prompt_submission',
                allowedModes: [
                  'text_prompt_submission',
                  'speech_to_speech_session',
                ],
                allowedRuntimeKinds: [
                  'external_text_agent',
                  'third_party_speech_to_speech',
                ],
                transcriptOwnership: 'hybrid',
              },
            ],
          },
        ),
        renderer: 'three_avatar',
        features: {
          requiresAuthGate: isPrivate !== false,
          supportsSpectators: false,
          supportsCallInAudio: false,
          supportsPolls: true,
          supportsVoting: true,
          supportsRealtimeTranscript: true,
          supportsCompanionAgents: true,
          supportsPresentationOverlay: false,
          supportsPremiumAvatars: false,
          supportsWebStage: false,
          supportsProgramManifest: false,
          supportsBroadcast: false,
          supportsRecording: false,
          supportsSharedStageMedia: false,
          supportsStageFeedback: false,
          supportsSkills: false,
        },
      };
    case 'townhall':
      return {
        ...TOWNHALL_ROOM_DEFINITION,
      };
    case 'concierge':
      return {
        kind: roomKind,
        experience: 'private_concierge',
        accessPolicy: 'private_auth',
        orchestrationMode: 'single_agent',
        participation: PRIMARY_STAGE_PARTICIPATION,
        companionRuntime: 'telegamez_turn_based',
        contribution: createRoomContributionPolicy(
          'live_turn',
          ['telegamez_turn_based'],
          {
            overrides: [
              {
                participantType: 'external_participant',
                defaultMode: 'text_prompt_submission',
                allowedModes: [
                  'text_prompt_submission',
                  'speech_to_speech_session',
                ],
                allowedRuntimeKinds: [
                  'external_text_agent',
                  'third_party_speech_to_speech',
                ],
                transcriptOwnership: 'hybrid',
              },
            ],
          },
        ),
        renderer: 'premium_avatar',
        features: {
          requiresAuthGate: true,
          supportsSpectators: false,
          supportsCallInAudio: false,
          supportsPolls: false,
          supportsVoting: false,
          supportsRealtimeTranscript: true,
          supportsCompanionAgents: true,
          supportsPresentationOverlay: false,
          supportsPremiumAvatars: true,
          supportsWebStage: false,
          supportsProgramManifest: false,
          supportsBroadcast: false,
          supportsRecording: false,
          supportsSharedStageMedia: false,
          supportsStageFeedback: false,
          supportsSkills: false,
        },
      };
    case 'training':
      return {
        kind: roomKind,
        experience: 'standard_room',
        accessPolicy: getDefaultAccessPolicy(roomKind, isPrivate),
        orchestrationMode: 'single_agent',
        participation: PRIMARY_STAGE_PARTICIPATION,
        companionRuntime: 'telegamez_turn_based',
        contribution: createRoomContributionPolicy(
          'live_turn',
          ['telegamez_turn_based'],
          {
            overrides: [
              {
                participantType: 'external_participant',
                defaultMode: 'text_prompt_submission',
                allowedModes: [
                  'text_prompt_submission',
                  'speech_to_speech_session',
                ],
                allowedRuntimeKinds: [
                  'external_text_agent',
                  'third_party_speech_to_speech',
                ],
                transcriptOwnership: 'hybrid',
              },
            ],
          },
        ),
        renderer: 'three_avatar',
        features: {
          requiresAuthGate: isPrivate !== false,
          supportsSpectators: false,
          supportsCallInAudio: false,
          supportsPolls: false,
          supportsVoting: false,
          supportsRealtimeTranscript: true,
          supportsCompanionAgents: true,
          supportsPresentationOverlay: false,
          supportsPremiumAvatars: false,
          supportsWebStage: false,
          supportsProgramManifest: false,
          supportsBroadcast: false,
          supportsRecording: false,
          supportsSharedStageMedia: false,
          supportsStageFeedback: false,
          supportsSkills: false,
        },
      };
    case 'standard':
    default:
      return {
        kind: 'standard',
        experience: 'standard_room',
        accessPolicy: getDefaultAccessPolicy('standard', isPrivate),
        orchestrationMode: 'single_agent',
        participation: PRIMARY_STAGE_PARTICIPATION,
        companionRuntime: 'telegamez_turn_based',
        contribution: createRoomContributionPolicy(
          'live_turn',
          ['telegamez_turn_based'],
          {
            overrides: [
              {
                participantType: 'external_participant',
                defaultMode: 'text_prompt_submission',
                allowedModes: [
                  'text_prompt_submission',
                  'speech_to_speech_session',
                ],
                allowedRuntimeKinds: [
                  'external_text_agent',
                  'third_party_speech_to_speech',
                ],
                transcriptOwnership: 'hybrid',
              },
            ],
          },
        ),
        renderer: 'three_avatar',
        features: {
          requiresAuthGate: isPrivate !== false,
          supportsSpectators: false,
          supportsCallInAudio: false,
          supportsPolls: false,
          supportsVoting: false,
          supportsRealtimeTranscript: true,
          supportsCompanionAgents: true,
          supportsPresentationOverlay: false,
          supportsPremiumAvatars: false,
          supportsWebStage: false,
          supportsProgramManifest: false,
          supportsBroadcast: false,
          supportsRecording: false,
          supportsSharedStageMedia: true,
          supportsStageFeedback: true,
          supportsSkills: false,
        },
      };
  }
};

const parseParticipationCapabilities = (
  value: unknown,
): ParticipationCapabilities | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const capabilityKeys: Array<keyof ParticipationCapabilities> = [
    'canSpeakLive',
    'canDialInAudio',
    'canVote',
    'canRespondToPolls',
    'canChat',
    'canRaiseHand',
    'canRequestPromotion',
    'canModeratePromotions',
    'canModerateContributions',
    'canMuteParticipants',
    'canCloseCallInWindow',
    'canManagePolls',
    'canBlockAudienceSessions',
    'receivesTranscript',
    'receivesRealtimeState',
  ];

  const parsedCapabilities = {} as ParticipationCapabilities;
  for (const key of capabilityKeys) {
    if (typeof value[key] !== 'boolean') {
      return undefined;
    }
    parsedCapabilities[key] = value[key] as boolean;
  }

  return parsedCapabilities;
};

const parseParticipationLayerDefinition = (
  value: unknown,
): ParticipationLayerDefinition | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  if (
    !isOneOf(PARTICIPATION_LAYER_KINDS, value.kind) ||
    typeof value.label !== 'string' ||
    !isOneOf(PRESENCE_MODES, value.presenceMode)
  ) {
    return undefined;
  }

  const capabilities = parseParticipationCapabilities(value.capabilities);
  if (!capabilities) {
    return undefined;
  }

  return {
    kind: value.kind,
    label: value.label,
    presenceMode: value.presenceMode,
    capabilities,
    maxParticipants:
      typeof value.maxParticipants === 'number' ? value.maxParticipants : undefined,
  };
};

const parseContributionPolicyOverride = (
  value: unknown,
): RoomContributionPolicyOverride | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const parsedOverride: RoomContributionPolicyOverride = {};
  if (value.participantType !== undefined) {
    if (!isOneOf(CONTRIBUTION_PARTICIPANT_TYPES, value.participantType)) {
      return undefined;
    }
    parsedOverride.participantType = value.participantType;
  }
  if (value.layer !== undefined) {
    if (!isOneOf(PARTICIPATION_LAYER_KINDS, value.layer)) {
      return undefined;
    }
    parsedOverride.layer = value.layer;
  }
  if (value.defaultMode !== undefined) {
    if (!isOneOf(CONTRIBUTION_MODES, value.defaultMode)) {
      return undefined;
    }
    parsedOverride.defaultMode = value.defaultMode;
  }
  if (value.allowedModes !== undefined) {
    if (!Array.isArray(value.allowedModes) || !value.allowedModes.every((mode) => isOneOf(CONTRIBUTION_MODES, mode))) {
      return undefined;
    }
    parsedOverride.allowedModes = value.allowedModes;
  }
  if (value.allowedRuntimeKinds !== undefined) {
    if (
      !Array.isArray(value.allowedRuntimeKinds) ||
      !value.allowedRuntimeKinds.every((runtimeKind) => isOneOf(COMPANION_RUNTIME_KINDS, runtimeKind))
    ) {
      return undefined;
    }
    parsedOverride.allowedRuntimeKinds = value.allowedRuntimeKinds as readonly CompanionRuntimeKind[];
  }
  if (value.transcriptOwnership !== undefined) {
    if (!isOneOf(TRANSCRIPT_OWNERSHIP_MODES, value.transcriptOwnership)) {
      return undefined;
    }
    parsedOverride.transcriptOwnership = value.transcriptOwnership;
  }

  return parsedOverride;
};

const parseContributionPolicy = (
  value: unknown,
): RoomContributionPolicy | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  if (
    !isOneOf(CONTRIBUTION_MODES, value.defaultMode) ||
    !Array.isArray(value.allowedModes) ||
    !value.allowedModes.every((mode) => isOneOf(CONTRIBUTION_MODES, mode)) ||
    !Array.isArray(value.allowedRuntimeKinds) ||
    !value.allowedRuntimeKinds.every((runtimeKind) => isOneOf(COMPANION_RUNTIME_KINDS, runtimeKind)) ||
    !isOneOf(TRANSCRIPT_OWNERSHIP_MODES, value.transcriptOwnership)
  ) {
    return undefined;
  }

  const overrides = value.overrides === undefined
    ? undefined
    : Array.isArray(value.overrides)
      ? value.overrides
          .map((override) => parseContributionPolicyOverride(override))
          .filter((override): override is RoomContributionPolicyOverride => Boolean(override))
      : undefined;

  return {
    defaultMode: value.defaultMode,
    allowedModes: value.allowedModes as readonly (typeof CONTRIBUTION_MODES)[number][],
    allowedRuntimeKinds: value.allowedRuntimeKinds as readonly CompanionRuntimeKind[],
    transcriptOwnership: value.transcriptOwnership,
    overrides,
  };
};

const parseFeatureOverrides = (
  value: unknown,
): Partial<RoomFeatureFlags> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const parsedFeatures: Partial<RoomFeatureFlags> = {};
  for (const key of FEATURE_FLAG_KEYS) {
    if (typeof value[key] === 'boolean') {
      parsedFeatures[key] = value[key] as boolean;
    }
  }

  return Object.keys(parsedFeatures).length > 0 ? parsedFeatures : undefined;
};

const parseRoomDefinitionExtension = (
  value: unknown,
): RoomDefinitionExtension | null => {
  if (!isRecord(value)) {
    return null;
  }

  const parsedExtension: RoomDefinitionExtension = {};
  if (value.experience !== undefined && isOneOf(ROOM_EXPERIENCES, value.experience)) {
    parsedExtension.experience = value.experience;
  }
  if (value.accessPolicy !== undefined && isOneOf(ACCESS_POLICIES, value.accessPolicy)) {
    parsedExtension.accessPolicy = value.accessPolicy;
  }
  if (
    value.orchestrationMode !== undefined &&
    isOneOf(ORCHESTRATION_MODES, value.orchestrationMode)
  ) {
    parsedExtension.orchestrationMode = value.orchestrationMode;
  }
  if (
    value.participation !== undefined &&
    Array.isArray(value.participation)
  ) {
    const parsedParticipation = value.participation
      .map((layer) => parseParticipationLayerDefinition(layer))
      .filter((layer): layer is ParticipationLayerDefinition => Boolean(layer));
    if (parsedParticipation.length > 0) {
      parsedExtension.participation = parsedParticipation;
    }
  }
  if (
    value.companionRuntime !== undefined &&
    isOneOf(COMPANION_RUNTIME_KINDS, value.companionRuntime)
  ) {
    parsedExtension.companionRuntime = value.companionRuntime;
  }
  if (value.contribution !== undefined) {
    const parsedContribution = parseContributionPolicy(value.contribution);
    if (parsedContribution) {
      parsedExtension.contribution = parsedContribution;
    }
  }
  if (value.renderer !== undefined && isOneOf(RENDERER_KINDS, value.renderer)) {
    parsedExtension.renderer = value.renderer;
  }
  if (value.features !== undefined) {
    const parsedFeatures = parseFeatureOverrides(value.features);
    if (parsedFeatures) {
      parsedExtension.features = parsedFeatures;
    }
  }

  return Object.keys(parsedExtension).length > 0 ? parsedExtension : null;
};

const getWorkflowRoomDefinitionExtension = (
  workflowConfiguration?: Record<string, unknown> | null,
): RoomDefinitionExtension | null => parseRoomDefinitionExtension(
  isRecord(workflowConfiguration) ? workflowConfiguration.roomDefinition : null,
);

const deriveRoomFeatures = (
  accessPolicy: AccessPolicy,
  participation: readonly ParticipationLayerDefinition[],
  features: RoomFeatureFlags,
  featureOverrides?: Partial<RoomFeatureFlags>,
): RoomFeatureFlags => ({
  ...features,
  ...featureOverrides,
  requiresAuthGate: accessPolicy === 'private_auth',
  supportsSpectators:
    features.supportsSpectators ||
    accessPolicy === 'spectator_join' ||
    accessPolicy === 'broadcast_audience' ||
    participation.some((layer) => layer.kind === 'audience_gallery'),
});

export const buildRoomDefinition = ({
  room,
  workflowConfiguration,
  roomExtension,
}: BuildRoomDefinitionInput): RoomDefinition => {
  const roomKind = normalizeRoomKind(room.roomType);
  const baseDefinition = getBaseRoomDefinition(roomKind, room.isPrivate);
  const resolvedExtension =
    roomExtension ?? getWorkflowRoomDefinitionExtension(workflowConfiguration);
  const accessPolicy =
    resolvedExtension?.accessPolicy ?? baseDefinition.accessPolicy;
  const participation =
    resolvedExtension?.participation ?? baseDefinition.participation;

  return {
    ...baseDefinition,
    experience: resolvedExtension?.experience ?? baseDefinition.experience,
    accessPolicy,
    orchestrationMode:
      resolvedExtension?.orchestrationMode ??
      baseDefinition.orchestrationMode,
    participation,
    companionRuntime:
      resolvedExtension?.companionRuntime ??
      baseDefinition.companionRuntime,
    contribution:
      resolvedExtension?.contribution ??
      baseDefinition.contribution,
    renderer: resolvedExtension?.renderer ?? baseDefinition.renderer,
    features: deriveRoomFeatures(
      accessPolicy,
      participation,
      baseDefinition.features,
      resolvedExtension?.features,
    ),
  };
};
